import { Calendar, Check, ChevronRight, FileText } from 'lucide-react';
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { setCurrentStep } from '../../redux/Slices/InterviewSlice';
import {
  setPlansPopUpOpen,
  setpopUpPlanMessage
} from '../../redux/Slices/PaymentsSlice';
import { useAppDispatch, useAppSelector } from '../../redux/store/hooks';
import {
  useHrGenerateTestMutation,
  useLazyFetchHRTestsQuery
} from '../../services/hrApi';
import UpgradePlanPopUP from '../ReUsableComponents/UpgradePlanPopUP';

const HrDashboard = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { userId } = useAppSelector(state => state.root.auth);
  const [hrGenerateTest, { isLoading }] = useHrGenerateTestMutation();
  const [fetchHRTests, { data, isSuccess }] = useLazyFetchHRTestsQuery({});

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return {
      date: date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      }),
      time: date.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit'
      })
    };
  };
  const showFullTestDetails = (id: string) => {
    navigate(`/hr-interview-results/${id}/${userId}`);
  };

  const handleHRInterviewStart = async () => {
    try {
      const res = await hrGenerateTest({}).unwrap();
      dispatch(setCurrentStep('instructions'));
    } catch (error: any) {
      if (error.status === 402) {
        const message = error?.data?.message;
        dispatch(setpopUpPlanMessage(message));
        dispatch(setPlansPopUpOpen(true));
      }
      console.error(error, 'Failed to Genrate Test');
    }
  };

  useEffect(() => {
    const page = 0;
    const size = 5;
    fetchHRTests({ size, page, userId });
  }, []);

  return (
    <div className='flex flex-col gap-3 p-5'>
      <div className='flex flex-row items-center justify-between gap-3'>
        <h2 className='text-center text-sm font-medium text-gray-900'>
          Manage all your interview rounds, track progress, and view your
          results.
        </h2>
      </div>
      <div className='flex flex-col justify-between gap-5 md:flex-row'>
        <p className='flex items-center gap-3 rounded-md bg-red-50 p-5'>
          <Check className='h-7 w-7 rounded-full bg-blue-300 p-1 text-blue-500' />
          <div>
            Completed Interview
            <p className='text-2xl font-bold'>{data?.totaltests ?? 0}</p>
          </div>
        </p>
        <button
          className='h-14 rounded-xl bg-primary px-6 py-3 font-semibold text-white shadow-lg transition-all duration-200 hover:from-green-600 hover:to-blue-700'
          onClick={() => handleHRInterviewStart()}
        >
          Start Test
        </button>
      </div>
      <div className='flex flex-col gap-3'>
        {data?.data?.length > 0 && (
          <p className='py-5'>Results from Your Previous 5 Interviews:</p>
        )}
        {data?.data?.length > 0 ? (
          data.data.map((test: any) => {
            const formattedDate = formatDate(test.createdDate);

            return (
              <>
                <div
                  key={test.id}
                  onClick={() => showFullTestDetails(test.id)}
                  className='group flex cursor-pointer items-center justify-between rounded-lg border border-gray-200 p-1 transition-all hover:border-purple-300 hover:bg-purple-50 md:p-4'
                >
                  <div className='flex items-center gap-4'>
                    <div className='rounded-lg bg-gradient-to-r from-purple-500 to-blue-500 p-3 text-white'>
                      <FileText size={20} />
                    </div>
                    <div>
                      <h3 className='font-medium text-gray-800 transition-colors group-hover:text-purple-700'>
                        {test.testName}
                      </h3>
                      <div className='mt-1 flex items-center gap-4 text-sm text-gray-600'>
                        <div className='flex items-center gap-1'>
                          <Calendar size={14} />
                          {formattedDate.date}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className='flex items-center text-purple-600 transition-colors group-hover:text-purple-800'>
                    <span className='mr-2 text-sm font-medium'>
                      View Results
                    </span>
                    <ChevronRight size={20} />
                  </div>
                </div>
              </>
            );
          })
        ) : (
          <div className='flex flex-col items-center justify-center rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 p-12 text-center'>
            <FileText className='mb-4 h-16 w-16 text-gray-400' />
            <h3 className='mb-2 text-lg font-medium text-gray-900'>
              No Interviews Yet
            </h3>
            <p className='mb-4 text-sm text-gray-600'>
              You haven&apos;t completed any interviews. Start your first test
              here.
            </p>
          </div>
        )}
      </div>
      <UpgradePlanPopUP />
    </div>
  );
};

export default HrDashboard;
